import {useEffect, useState, useMemo} from 'react'
import format from 'date-fns/format'
import setDateParts from 'date-fns/set'
import isValid from 'date-fns/isValid'

import DatePicker from '../../../common/components/DatePicker.js'
import parseTime from '../../../common/parseTime.js'
import TimeInput from '../../../common/components/TimeInput.js'
import {
  updateLabelConfig,
  labelConfigSelector,
  isLabelPropertyEnabledSelector,
} from '../../../data/labelInfos/index.js'
import {useSelector} from '../../../store.js'
import {AMAZON_SHIPPER} from '../../../common/constants/ShipperNames.js'
import useLabelProperty, {
  defaultValidLabelPropertiesFunc,
  ADDITIONAL_DEPENDENT_PROPERTIES_FUNC,
  formatMetaClassName,
} from './useLabelProperty.js'
import EnableLabelProperty, {PickLabelProperty} from './EnableLabelProperty.js'
import className from '../../../common/className.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'

const VALID_SHIPPER_TYPES = [AMAZON_SHIPPER]
const LABEL_PROPERTY_PICKUP_START_DATE = 'pickup_start_date'
const LABEL_PROPERTY_PICKUP_END_DATE = 'pickup_end_date'

function labelPropertiesFunc() {
  return [LABEL_PROPERTY_PICKUP_START_DATE, LABEL_PROPERTY_PICKUP_END_DATE]
}

export default function PickupDateRange() {
  const {
    labelInfoID,
    shipperType,
    onChange,
    isLabelEnableMode,
    isSummary,
    isPickMode,
  } = useLabelConfigContext()
  const propertySpecs = PickupDateRange.propertySpecList[0]
  const [startLabelProperty, endLabelProperty] =
    propertySpecs.labelPropertiesFunc(shipperType, labelInfoID)
  const config = useSelector((state) =>
    labelConfigSelector(state, {labelInfoID}),
  )

  useLabelProperty({
    labelProperty: startLabelProperty,
    propertySpecs,
  })

  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty: startLabelProperty,
    }),
  )
  const pickup_start_date = config[startLabelProperty]
  const pickup_end_date = config[endLabelProperty]

  const [isInitial, setIsInitial] = useState(true)
  const [pickupDate, setPickupDate] = useState(null)
  const [pickupTime, setPickupTime] = useState('8:00 AM')
  const [closeTime, setCloseTime] = useState('5:30 PM')
  const pickupStartDate = useMemo(
    () => (pickup_start_date ? new Date(pickup_start_date) : null),
    [pickup_start_date],
  )
  const pickupEndDate = useMemo(
    () => (pickup_end_date ? new Date(pickup_end_date) : null),
    [pickup_end_date],
  )
  const [now] = useState(new Date())

  useEffect(() => {
    if (!isValid(pickupStartDate)) {
      setPickupDate(null)
      setPickupTime('8:00 AM')
      setCloseTime('5:30 PM')

      return
    }

    setPickupDate(pickupStartDate)
    setPickupTime(format(pickupStartDate, 'h:mm a'))
    setCloseTime(format(pickupEndDate, 'h:mm a'))
  }, [pickupStartDate, pickupEndDate])

  useEffect(() => {
    if (isInitial) {
      setIsInitial(false)

      return
    }

    if (!pickupDate || !isValid(pickupDate)) {
      if (pickupStartDate || pickupEndDate) {
        updateLabelConfig(labelInfoID, {
          [startLabelProperty]: null,
          [endLabelProperty]: null,
        })

        onChange()
      }

      return
    }

    updateLabelConfig(labelInfoID, {
      [startLabelProperty]: setDateParts(
        pickupDate,
        parseTime(pickupTime),
      ).toISOString(),
      [endLabelProperty]: setDateParts(
        pickupDate,
        parseTime(closeTime),
      ).toISOString(),
    })

    onChange()
  }, [pickupDate, pickupTime, closeTime])

  if (!VALID_SHIPPER_TYPES.includes(shipperType)) {
    return null
  }

  const label = 'Pickup Date'

  if (isSummary) {
    return label
  }

  if (isPickMode) {
    return (
      <PickLabelProperty label={label} labelProperty={startLabelProperty} />
    )
  }

  const disabled = isLabelEnableMode && !isLabelPropertyEnabled

  return (
    <li
      className={className`list__item--shipping-options margin-bottom-15 ${formatMetaClassName(
        startLabelProperty,
      )} ${{
        'property-disabled': disabled,
      }}`}
    >
      <div className="wrap--edit-preset-form-input">
        <div>
          <label
            className="label--bold label--config__txt"
            htmlFor={`${labelInfoID}__expected_service_offering_date`}
          >
            {label}
          </label>
          <DatePicker
            id={`${labelInfoID}__expected_service_offering_date`}
            onChange={(date) => setPickupDate(date)}
            selected={pickupDate}
            minDate={now}
            disabled={disabled}
          />
        </div>
        {pickupDate && (
          <div className="list">
            <div
              className={className`margin-bottom-0 margin-top-10 ${
                !isLabelEnableMode && 'margin-right-10'
              }`}
            >
              <div>
                <label
                  className="label--bold label--config__txt"
                  htmlFor="id_earliest_time"
                >
                  Earliest Time
                </label>
                <TimeInput
                  id="earliest_time"
                  value={pickupTime}
                  onBlur={(time) => setPickupTime(time)}
                  disabled={disabled}
                />
              </div>
            </div>
            <div className="margin-bottom-0 margin-top-10">
              <div>
                <label
                  className="label--bold label--config__txt"
                  htmlFor="id_latest_time"
                >
                  Latest Time
                </label>
                <TimeInput
                  id="latest_time"
                  value={closeTime}
                  onBlur={(time) => setCloseTime(time)}
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <EnableLabelProperty labelProperty={startLabelProperty} />
    </li>
  )
}

PickupDateRange.propertySpecList = [
  {
    validShipperTypes: VALID_SHIPPER_TYPES,
    labelPropertiesFunc,
    dependentPropertiesFunc: ADDITIONAL_DEPENDENT_PROPERTIES_FUNC,
    validLabelPropertiesFunc: defaultValidLabelPropertiesFunc,
  },
]
